import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {useNavigate} from "react-router";
import Api from "../../services/Api";
import { getLanguage } from "../../lang/helpers";
import iconInfo from "url:shared-assets/icons/white/information-circle.svg";
import AutocompleteSearch from "../../components/AutocompleteSearch";
import iconUsers from "url:shared-assets/icons/neutral-400/user-group.svg";
import iconCheck from "url:shared-assets/icons/checkmark.svg";
import iconBadgeCheck from "url:shared-assets/icons/green/completed.svg";
import Checkbox from "../../components/Checkbox";
import CustomButton from "../../components/CustomButton";
import Tooltip from "../../components/Tooltip";
import IconButton from "../../components/IconButton";
import starOutlineIcon from "url:shared-assets/icons/neutral-400/star-outline.svg";
import starFilledIcon from "url:shared-assets/icons/primary-500/star-filled.svg";
import animationProcessing from "url:shared-assets/animations/processing.svg";
import { Modal, ModalHeader, ModalBody } from "../../components/Modal";
import {CONFIG} from "../../constants";
import { VisionStatementInterface } from "../DevelopmentPlan/interfaces";

interface FeedbackUserInterface {
  email: string;
  name: string;
  favorite: boolean;
  selected: boolean;
}

interface Props {
  visionStatement: VisionStatementInterface;
  done: Function;
}


const SelfAssessmentRecipientPage = (props: Props) => {
  const api = Api.getInstance();
  const navigate = useNavigate();
  const {t, i18n} = useTranslation(); 
  const lang = getLanguage(i18n.language);
  const [loading, setLoading] = useState(true);
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [waiting, setWaiting] = useState(false);
  const [fakeDone, setFakeDone] = useState(false);
  const [done, setDone] = useState(false);
  const [demo, setDemo] = useState(false);
  const [feedbackUsers, setFeedbackUsers] = useState<Array<FeedbackUserInterface>>([]);
  const [deadline, setDeadline] = useState("-");
  const [answered, setAnswered] = useState(0);
  const [error, setError] = useState("");
  const [id, setId] = useState(props.visionStatement.surveyRequests[0].id)

  useEffect(() => {
    getData();
  }, [])

  const getData = () => {
    api.get("client/survey_request_invite/" + id).then(response => {
      if (response.json.status === "ok") {
        setFeedbackUsers(response.json.feedbackUsers);
        setDeadline(response.json.deadline);
        setAnswered(response.json.answered);
        setDemo(response.json.demo);
      } else if (response.json.error) {
        setError(response.json.error);
      } else {
        setError("Unknown error");
      }
      setLoading(false);
    });
  }

  const submit = () => {
    const data = {
      feedbackUsers: feedbackUsers,
      fake_done: fakeDone
    }
    setWaiting(true);
    api.post("client/survey_request_feedback/" + id, data).then(response => {
      if (response.json.status === "ok") {
        setDone(true);
        props.done();
      }
      setWaiting(false);
    })
  }

  if (loading) {
    return (<div className="mt-4">Loading...</div>);
  }

  if (done) {
    return (
      <div className="main-one-col small">
        <div className="mt-4 text-center mx-auto" style={{width: 358}}>
          <img src={iconBadgeCheck} alt="done" className="mt-10 w-20 h-20 mx-auto"/>
          <div className="mt-6 z-heading1">
            {t("feedback-request-sent")}
          </div>

          <div className="mt-2 text-gray-300">
            {
              lang === "sv" ? "Bra jobbat! Vi hör av oss när ditt feedbackresultat är redo, senast " :
                "Well done! We’ll reach out when the feedback result is ready, likely by "
            }
            {deadline.substring(0, 10)}.
          </div>
        </div>

        <div className="mt-6 text-center mx-auto" style={{width: 300}}>
          <div className="mt-2">
            <CustomButton buttonType="transparentBase" style={{width: "unset"}} className="mx-auto"
                          onClick={() => navigate('/dev-plan/long-term-goal/' + props.visionStatement.id)}>
              {lang === "sv" ? "Återgå till målet" : "Return to goal"}
            </CustomButton>
          </div>

        </div>
      </div>
    )
  }

  const formValid = () => {
    let foundSelected = feedbackUsers.filter(f => f.selected).length
    return (foundSelected + answered) >= 3;
  }

  const changeFeedbackUser = (email: string, action: string) => {
    const _feedbackUsers = [...feedbackUsers];
    for (const feedbackUser of _feedbackUsers) {
      if (feedbackUser.email === email) {
        if (action === "selected") {
          feedbackUser.selected = !feedbackUser.selected;
        } else if (action === "favorite") {
          feedbackUser.favorite = !feedbackUser.favorite;
        }
        break;
      }
    }
    setFeedbackUsers(_feedbackUsers);
  };

    const renderInfoModel = () => {
    return (
      <Modal isOpen={showInfoModal} onClose={closeInfoModel}>
        <ModalHeader onClose={closeInfoModel}>
          <h1 className="z-heading1 text-white">
           {t("feedback-request")}
          </h1>
        </ModalHeader>
        <ModalBody>
          <p className="mb-4 z-body1">{t("self-assessment-recipient-info1")}</p>
          <p className="z-body1">{(t("self-assessment-recipient-info2"))}</p>
        </ModalBody>
      </Modal>
    );
  };

const closeInfoModel = () => {
    setShowInfoModal(false);
  };

  return (
    <div className="main-one-col small">
      <div className="flex items-center mt-6">
        <div className="z-h2">{t("feedback-request")}</div>
        <img src={iconInfo} alt="info" className="ml-2 p-1 cursor-pointer hover:bg-indigo-700 rounded-[20px]"
             onClick={() => setShowInfoModal(!showInfoModal)}/>
      </div>
      {renderInfoModel()}

      <div className="mt-6">
        <p className="mb-2 font-medium">
          {t("feedback-search-recipients")}
        </p>
        <AutocompleteSearch
          options={feedbackUsers.filter(
            (fu) => !fu.selected && !fu.favorite
          )}
          getOptionLabel={(contact) => `${contact.name} (${contact.email})`}
          getOptionKey={(contact) => contact.email}
          placeholder={t("feedback-search-by-name")}
          onSelect={(_feedbackUser) =>
            changeFeedbackUser(_feedbackUser.email, "selected")
          }
        />

        {feedbackUsers.filter((fu) => fu.selected || fu.favorite).length >
        0 ? (
          <div className="my-6">
            <div className="z-title2">
              {t("recipients")}
            </div>
            <p className="text-gray-400">
              {t("feedback-recipients-subtitle")}
            </p>
          </div>
        ) : (
          <div className="border border-dashed border-indigo-700 mt-4 mb-4 rounded-md text-center py-10">
            <img
              src={iconUsers}
              alt="users"
              className="h-6 w-6 mx-auto mb-4"
            />

            <div className="z-body1 text-gray-400!">
              <p>
                {lang === "sv"
                  ? "Inga mottagare valda."
                  : "No recipients selected."}
              </p>

              <p className="mt-1 z-body1 text-gray-400!">
                {lang === "sv"
                  ? "Välj minst 3 för att fortsätta."
                  : "Please select at least 3 to continue."}
              </p>
            </div>
          </div>
        )}

        {feedbackUsers
          .filter((fu) => fu.selected || fu.favorite)
          .map((recipient, index) => (
            <div
              className="mt-2 flex items-center justify-between"
              key={"recipient_" + index}
            >
              <div className="flex items-center">
                <div
                  className={
                    "h-6 w-6 rounded flex items-center justify-center cursor-pointer " +
                    (recipient.selected
                      ? "bg-primary-500"
                      : "border border-gray-400")
                  }
                  onClick={() =>
                    changeFeedbackUser(recipient.email, "selected")
                  }
                >
                  {recipient.selected && (
                    <img
                      src={iconCheck}
                      className="h-5 w-5"
                      alt="Checked"
                    />
                  )}
                </div>
                <p className="ml-2">
                  {recipient.name} ({recipient.email})
                </p>
              </div>
              <Tooltip
                text={t(
                  recipient.favorite
                    ? "remove-as-favourite"
                    : "add-as-favourite"
                )}
                tooltipPosition="left"
              >
                <IconButton
                  icon={
                    recipient.favorite ? starFilledIcon : starOutlineIcon
                  }
                  onClick={() =>
                    changeFeedbackUser(recipient.email, "favorite")
                  }
                />
              </Tooltip>
            </div>
          ))}
      </div>

      {
        (answered > 0) &&
        <div className="mt-6 border border-dashed border-gray-400 p-4 rounded-md">
          {
            lang === "sv" ? "Vi har exkluderat de mottagare som redan svarat på denna förfrågan." :
              "We have excluded the respondents who have answered this feedback request."
          }
        </div>
      }

      {
        waiting ? (
          <div className="mt-6 text-center">
            <img
              src={animationProcessing}
              alt="processing"
              className="mx-auto"
              style={{height: 16, width: 114}}
            />
          </div>
        ) : (
          <div className="mt-6 text-center">
            <CustomButton
              buttonType="blueBase"
              className="mx-auto"
              disabled={!formValid()}
              onClick={() => submit()}
              style={{width: "unset"}}
            >
              {lang === "sv" ? "Skicka feedbackförfrågan" : "Send feedback request"}
            </CustomButton>
          </div>
        )
      }

      {
        (!demo && CONFIG.SYSTEM !== "production") &&
          <div className="mt-6">
              <Checkbox id="fake_done" label="Debug: Simulate responses"
                        onChange={() => setFakeDone(!fakeDone)} checked={fakeDone}/>
          </div>
      }

      {
        demo &&
          <div className="mt-6 text-red-500">
            {
              lang === "sv" ? "Detta är ett demokonto - inga feedbackförfrågningar kommer att skickas. " +
                "Du kommer att få låtsasfeedback." :
                "This is a demo account - no feedback requests will be sent. You will get mocked feedback."
            }
          </div>
      }

    </div>
  )
}

export default SelfAssessmentRecipientPage;